import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_QUERY_KEYS } from 'consts';
import { useUpdateDocumentCollectionKey } from '../helpers/use-update-document-collection-key';
import { putDocumentsSignaturePosition } from '../requests';
import type {
  DocumentDetails,
  PutDocumentsSignaturePositionRequestType,
} from '../types';

export type DocumentsSignatureProps = Omit<
  PutDocumentsSignaturePositionRequestType,
  'documentId'
> & {
  documentUserId?: string;
  documentId?: PutDocumentsSignaturePositionRequestType['documentId'];
};

export type usePutDocumentsSignaturePositionProps = {
  documentId?: PutDocumentsSignaturePositionRequestType['documentId'];
  userId?: number;
  documentUserId?: string;
};

/**
 * Custom hook to update the signature position of a document for the current user.
 * @param props - The properties for the hook.
 */
export const usePutDocumentsSignaturePosition = (
  props: usePutDocumentsSignaturePositionProps = {},
) => {
  const { documentId, userId } = props;
  const queryClient = useQueryClient();
  const updateCollectionCache = useUpdateDocumentCollectionKey();

  /**
   * Method to update the query client with the new signature position.
   * @param props - The properties for the signature position.
   * @param documentUserId - The document user ID.
   */
  const updateQueryClient = async (
    props: DocumentsSignatureProps,
    documentUserId: string = '',
  ) => {
    // update the document data
    queryClient.setQueryData(
      [API_QUERY_KEYS.document, { documentId: props.documentId || documentId }],
      (oldData: DocumentDetails | undefined) => {
        let signaturePositions = oldData?.signaturePositions || [];

        const newSignaturePosition = {
          userId: userId!,
          x: props.x,
          y: props.y,
          page: props.page,
          dpi: 120,
          documentUserId,
        };

        if (signaturePositions.length === 0) {
          signaturePositions = [newSignaturePosition];
        } else {
          let updated = false;
          signaturePositions = signaturePositions.map(position => {
            if (position.userId === userId) {
              updated = true;
              return {
                ...position,
                x: props.x,
                y: props.y,
                page: props.page,
              };
            }

            return position;
          }) as any;

          if (!updated) {
            signaturePositions = [...signaturePositions, newSignaturePosition];
          }
        }

        return oldData && userId
          ? ({
              ...oldData,
              signaturePositions,
            } as DocumentDetails)
          : oldData;
      },
    );
  };

  return useMutation({
    mutationFn: ({ documentUserId, ...data }: DocumentsSignatureProps) => {
      const documentId = data.documentId || props.documentId!;
      // optimistically update the query client with the new signature position
      updateQueryClient(data, props.documentUserId || documentUserId);
      updateCollectionCache(documentId);

      return putDocumentsSignaturePosition({
        documentId: documentId!,
        ...data,
      });
    },
  });
};
